import {
  ChatApiInterface,
  ChatConversationScope,
  ChatAttachmentApiInterface,
  ChatMessage,
  ConversationAttachmentItem,
  ConversationItem,
} from 'shared/live-chat';
import { filter, firstValueFrom, map, Observable, tap, throwError } from 'rxjs';
import { LiveChatDtoCasts } from './message.model';
import { Injectable } from '@angular/core';
import { ChatService } from '../../api/gen';
import { AttachmentFileService } from '../services/file/attachment-file.service';
import { AssociatedCallPeriod } from '../model/attachment/associated-call-period';
import { AttachmentState } from '../scheduling/inquiry-details/attachments/data/attachment-state';
import { AttachmentService as HelperAttachmentService } from '../services/api/attachment.service';
import { AppointmentAttachmentsService } from '../video-appointment/services/appointment-attachments.service';
import { CallCtrlService } from '../video-appointment/services/call-ctrl.service';
import { Mode } from '../annotations/drawing-canvas/drawing-canvas.component';

// see https://stackoverflow.com/questions/51859038/angular-library-module-inject-service-with-abstract-class
@Injectable({
  providedIn: 'root',
})
export class HelperChatService
  implements ChatApiInterface, ChatAttachmentApiInterface
{
  constructor(
    private readonly liveChatService: ChatService,
    private readonly attachmentFileService: AttachmentFileService,
    private readonly attachmentService: HelperAttachmentService,
    private readonly appointmentAttachmentsService: AppointmentAttachmentsService,
    private readonly callCtrlService: CallCtrlService,
  ) {}

  fetchMessagesForInquiry(
    inquiryId: string,
    scope: ChatConversationScope,
  ): Observable<ChatMessage[]> {
    return this.fetchMessages(inquiryId, scope).pipe(
      map((data) => data.map((x) => LiveChatDtoCasts.castFromDto(x))),
    );
  }

  fetchConversationItemsForInquiry(
    inquiryId: string,
    scope: ChatConversationScope,
  ): Observable<ConversationItem[]> {
    return this.fetchConversationItems(inquiryId, scope).pipe(
      map((data) =>
        data.map((item) => LiveChatDtoCasts.castConversationItem(item)),
      ),
    );
  }

  sendMessage(
    inquiryId: string,
    scope: ChatConversationScope,
    newMessage: string,
  ): Observable<ChatMessage> {
    return this.send(inquiryId, scope, newMessage).pipe(
      map((x) => LiveChatDtoCasts.castFromDto(x)),
    );
  }

  deleteMessage(
    inquiryId: string,
    scope: ChatConversationScope,
    messageId: string,
  ): Observable<ChatMessage> {
    return this.delete(inquiryId, scope, messageId).pipe(
      map((x) => LiveChatDtoCasts.castFromDto(x)),
    );
  }

  uploadAttachments(
    inquiryId: string,
    scope: ChatConversationScope,
    files: File[],
  ): Observable<ConversationItem> {
    if (!('videoAppointmentId' in scope) || !scope.videoAppointmentId) {
      return throwError(
        () =>
          new Error(
            'Attachments can only be uploaded in an active Video Appointment chat.',
          ),
      );
    }

    return this.attachmentFileService
      .uploadAttachments(
        AssociatedCallPeriod.During,
        inquiryId,
        scope.videoAppointmentId,
        files,
      )
      .pipe(
        map((state) =>
          state.state === AttachmentState.SUCCESSFUL
            ? state.attachment
            : undefined,
        ),
        filter((attachment) => attachment != null),
        tap(() => {
          void this.appointmentAttachmentsService.refreshAttachmentsForAppointment(
            inquiryId,
          );
        }),
        map((attachment) => LiveChatDtoCasts.castAttachmentModel(attachment)),
      );
  }

  markChatMessageReceived(
    inquiryId: string,
    scope: ChatConversationScope,
    messageId: string,
  ): Observable<ChatMessage> {
    return this.markReceived(inquiryId, scope, messageId).pipe(
      map((x) => LiveChatDtoCasts.castFromDto(x)),
    );
  }

  markChatMessagesReceivedForInquiry(
    inquiryId: string,
    scope: ChatConversationScope,
  ): Observable<ChatMessage[]> {
    return this.markAllReceived(inquiryId, scope).pipe(
      map((x) => x.map((dto) => LiveChatDtoCasts.castFromDto(dto))),
    );
  }

  translateChatMessage(
    messageId: string,
    locale: string,
    scope: ChatConversationScope,
  ): Observable<ChatMessage> {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      if (!scope.inquiryId)
        return throwError(
          () =>
            new Error(
              'A Case identifier is required to translate a chat message.',
            ),
        );
      return this.liveChatService
        .chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdMessageIdTranslationsPatch(
          scope.inquiryId,
          scope.videoAppointmentId,
          messageId,
          { targetLocale: locale },
        )
        .pipe(map((x) => LiveChatDtoCasts.castFromDto(x)));
    }

    return throwError(
      () => new Error('Legacy Case-level chat history is read-only.'),
    );
  }

  getAttachment(attachmentId: string): Observable<ConversationAttachmentItem> {
    return this.attachmentService
      .getAttachment(attachmentId)
      .pipe(
        map((attachment) => LiveChatDtoCasts.castAttachmentModel(attachment)),
      );
  }

  async openAttachment(
    inquiryId: string,
    item: ConversationAttachmentItem,
  ): Promise<void> {
    await this.selectAttachment(inquiryId, item);
  }

  async presentAttachment(
    inquiryId: string,
    item: ConversationAttachmentItem,
  ): Promise<void> {
    await this.selectAttachment(inquiryId, item, true);
  }

  private async selectAttachment(
    inquiryId: string,
    item: ConversationAttachmentItem,
    startPresenting = false,
  ): Promise<void> {
    let attachments = this.appointmentAttachmentsService.attachments$.value;
    if (!attachments?.length) {
      await this.appointmentAttachmentsService.refreshAttachmentsForAppointment(
        inquiryId,
      );
      attachments = this.appointmentAttachmentsService.attachments$.value;
    }

    if (!attachments?.length) {
      return;
    }

    const index = attachments.findIndex(
      (attachment) => attachment.id === item.attachment.id,
    );
    if (index < 0) {
      return;
    }

    const selectedAttachment = attachments[index];
    const canBePresented =
      AppointmentAttachmentsService.canBePresented(selectedAttachment);
    if (startPresenting && !canBePresented) {
      return;
    }

    const mode = await firstValueFrom(this.callCtrlService.modeChannel$);
    if (mode === Mode.Annotate_Attachment) {
      return;
    }

    this.appointmentAttachmentsService.changeCurrentAttachment({
      attachments,
      currentIndex: index,
      canBePresented,
      isInPresentationMode: false,
    });

    if (startPresenting) {
      this.appointmentAttachmentsService.startPresentingAttachment();
    }
  }

  updateAttachmentNote(
    attachmentId: string,
    note?: string | null,
  ): Observable<ConversationAttachmentItem> {
    return this.attachmentService.updateAttachmentNote(attachmentId, note).pipe(
      tap(() => {
        void this.appointmentAttachmentsService.refreshAttachmentsForAppointment();
      }),
      map((attachment) => LiveChatDtoCasts.castAttachmentModel(attachment)),
    );
  }

  deleteAttachment(attachmentId: string): Observable<void> {
    return this.attachmentService.deleteAttachment(attachmentId).pipe(
      tap(() => {
        void this.appointmentAttachmentsService.refreshAttachmentsForAppointment();
      }),
      map((): void => undefined),
    );
  }

  private fetchMessages(inquiryId: string, scope: ChatConversationScope) {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      return this.liveChatService.chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdGet(
        inquiryId,
        scope.videoAppointmentId,
      );
    }

    return this.liveChatService.chatAgentInquiryIdLegacyGet(inquiryId);
  }

  private fetchConversationItems(
    inquiryId: string,
    scope: ChatConversationScope,
  ) {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      return this.liveChatService.chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdConversationGet(
        inquiryId,
        scope.videoAppointmentId,
      );
    }

    return this.liveChatService.chatAgentInquiryIdLegacyConversationGet(
      inquiryId,
    );
  }

  private send(
    inquiryId: string,
    scope: ChatConversationScope,
    message: string,
  ) {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      return this.liveChatService.chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdPost(
        inquiryId,
        scope.videoAppointmentId,
        { message },
      );
    }

    throw new Error('Case-level legacy chat is read-only.');
  }

  private delete(
    inquiryId: string,
    scope: ChatConversationScope,
    messageId: string,
  ) {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      return this.liveChatService.chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdMessageIdDelete(
        inquiryId,
        scope.videoAppointmentId,
        messageId,
      );
    }

    throw new Error('Case-level legacy chat is read-only.');
  }

  private markReceived(
    inquiryId: string,
    scope: ChatConversationScope,
    messageId: string,
  ) {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      return this.liveChatService.chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdMessageIdPut(
        inquiryId,
        scope.videoAppointmentId,
        messageId,
      );
    }

    throw new Error('Case-level legacy messages are already historical.');
  }

  private markAllReceived(inquiryId: string, scope: ChatConversationScope) {
    if ('videoAppointmentId' in scope && scope.videoAppointmentId) {
      return this.liveChatService.chatAgentInquiryIdVideoAppointmentsVideoAppointmentIdReceivedPut(
        inquiryId,
        scope.videoAppointmentId,
      );
    }

    throw new Error('Case-level legacy messages are already historical.');
  }
}
